import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {TicketingDashboardState, TicketingStatusFilterValue} from '@Components/event-ticketing/event-ticketing.types';
import {getDefaultOrdersLazyData, getDefaultTicketingPlanLimit, getDefaultTicketingRsvpStats, getDefaultTicketingStats} from '@Components/event-ticketing/event-ticketing.types';
import {eventTicketingExtraReducers} from '@Components/event-ticketing/event-ticketing-extra-reducers';
import {ALL_TICKETING_STATUS_FILTER_VALUES, getActiveOrdersList} from '@Libraries/event-ticketing.library';
import {getDummyEventItem} from '@Libraries/events.library';
import {LoadingStates} from '@Utils/loading.util';

// Lives here (not the types file) so the types module carries no value import of the events library — that import
// closed a module cycle that could leave the types module half-evaluated when a lazy chunk loaded it first.
const getDefaultTicketingDashboardState = (): TicketingDashboardState => {
  return {
    event: getDummyEventItem(),
    loadingState: LoadingStates.NOT_LOADED,
    hasFetchEventFailed: false,
    isTicketingUnavailableForEvent: false,
    stats: getDefaultTicketingStats(),
    rsvpStats: getDefaultTicketingRsvpStats(),
    freeStats: getDefaultTicketingRsvpStats(),
    statsLoadingState: LoadingStates.NOT_LOADED,
    ordersHashmap: {},
    defaultOrders: getDefaultOrdersLazyData(),
    filteredOrders: getDefaultOrdersLazyData(),
    search: '',
    liveSearchTerm: '',
    // Always-explicit filter: the default view has every status selected (the dropdown's "All").
    activeStatusFilters: [...ALL_TICKETING_STATUS_FILTER_VALUES],
    planLimit: getDefaultTicketingPlanLimit(),
  };
};

const eventTicketingSlice = createSlice({
  name: 'eventTicketing',
  initialState: getDefaultTicketingDashboardState(),
  reducers: {
    // A new search/filter is a fresh query, so the filtered list is reset; the lazy-load then fetches its first
    // page. The cached unfiltered list is left untouched (clearing the filter shows it again).
    setOrdersSearch: (state, {payload}: PayloadAction<string>) => {
      state.search = payload;
      state.liveSearchTerm = payload;
      state.filteredOrders = getDefaultOrdersLazyData();
    },
    // The term as typed: filters the already-loaded orders in place for instant feedback. The backend fetch only
    // happens on submit (setOrdersSearch) — except clearing the field, which drops any submitted term right away
    // (back to the full list without waiting for Enter). That clear-applies-immediately rule lives here so every
    // search field gets it by dispatching this one action.
    setOrdersLiveSearchTerm: (state, {payload}: PayloadAction<string>) => {
      state.liveSearchTerm = payload;
      if (payload === '' && state.search !== '') {
        state.search = '';
        state.filteredOrders = getDefaultOrdersLazyData();
      }
    },
    setActiveStatusFilters: (state, {payload}: PayloadAction<TicketingStatusFilterValue[]>) => {
      state.activeStatusFilters = payload;
      state.filteredOrders = getDefaultOrdersLazyData();
    },
    // Nudges the active list's loading state so the lazy-load effect fetches the next page. Reading live state
    // here (rather than a captured closure) keeps it correct after a filter change.
    triggerOrdersLazyLoadOnScroll: (state) => {
      const activeOrders = getActiveOrdersList(state, state.search, state.activeStatusFilters);
      if (activeOrders.currentPageNumber < activeOrders.lastPage && activeOrders.loadingState !== LoadingStates.LOADING) {
        activeOrders.loadingState = LoadingStates.NOT_LOADED;
      }
    },
  },
  extraReducers: eventTicketingExtraReducers,
});

export const {setOrdersSearch, setOrdersLiveSearchTerm, setActiveStatusFilters, triggerOrdersLazyLoadOnScroll} = eventTicketingSlice.actions;
export const eventTicketingReducer = eventTicketingSlice.reducer;
